import React, { ReactElement, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DynamicSaasOnboardingPricingTiers from 'src/app/Onboarding/DynamicSaasOnboardingPricingTiers'
import SaasOnboardingPricingTiers, {
  SaasOnboardingPricingTiersProps,
} from 'src/app/Onboarding/SaasOnboardingPricingTiers'
import BrandAgnosticFooter from 'src/components/BrandAgnosticFooter/BrandAgnosticFooter'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import { useQueryParams } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { connectedReplace } from 'src/navigation/Lnk'
import { accountOverviewUrl, homeUrl } from 'src/navigation/urls'
import {
  usePostCreateSubscription,
  usePostEnterpriseRequest,
} from 'src/queries/organizations/saas.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

import style from './PricingPage.style'

export default function PricingPage(): ReactElement {
  const [planLoadingStates, setPlanLoadingStates] = useState<
    SaasOnboardingPricingTiersProps['buttonLoadingStates']
  >({
    starter: false,
    pro: false,
    enterprise: false,
  })

  const dispatch = useDispatch()
  const user = useSelector(selectUserListModelFromState)
  const {
    enable_starter_in_chargebee: enableSaasPricingPlanSelection,
    show_dynamic_pricing_page: showDynamicPricingPage,
  } = useSelector(selectFeatureFlaggerFromState)
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const queryParams = useQueryParams()
  const cardId = queryParams['cardId'] as string | undefined

  const { mutate: postCreateSubscription } = usePostCreateSubscription()

  const { mutateAsync: sendPostEnterpriseRequest } = usePostEnterpriseRequest()

  const redirect = (): void => {
    if (cardId !== undefined) {
      // Redirect to newly registered card on success
      dispatch(connectedReplace(accountOverviewUrl(cardId)))
    } else {
      // Redirect to home on success
      dispatch(connectedReplace(homeUrl()))
    }
  }

  return (
    <div css={style} className="PricingPage">
      <div className="logo-container">
        <BrandPrimaryLogo />
      </div>
      <div className="SaasOnboardingPricingTiers-container flex-column justify-center align-center">
        {showDynamicPricingPage ? (
          <DynamicSaasOnboardingPricingTiers />
        ) : (
          <SaasOnboardingPricingTiers
            buttonLoadingStates={planLoadingStates}
            onClickPlanButton={async (planPeriod, planType): Promise<void> => {
              analytics.track(
                `onboarding ${planType.toLowerCase()} tier selected`,
                {
                  orgId: user?.organizationId,
                  userId: user?.id,
                },
              )
              switch (planType) {
                case 'STARTER':
                  if (enableSaasPricingPlanSelection && user?.organizationId) {
                    setPlanLoadingStates({
                      starter: true,
                      enterprise: false,
                      pro: false,
                    })

                    postCreateSubscription(
                      {
                        orgId: user.organizationId,
                        redirect: false,
                        body: {
                          billingPeriod: planPeriod,
                          plan: 'STARTER',
                        },
                      },
                      {
                        onSuccess: () => {
                          redirect()
                        },
                      },
                    )
                  } else {
                    redirect()
                  }
                  break
                case 'PRO':
                  // By this time in the user flow, the user's org should be updated via Redux, so we can
                  // assume that if the `organizationSubscriptionUrlModel` is truthy, we can
                  // use those links and finish up the flow here
                  if (user?.organizationId) {
                    setPlanLoadingStates({
                      starter: false,
                      enterprise: false,
                      pro: true,
                    })

                    postCreateSubscription({
                      orgId: user.organizationId,
                      redirect: true,
                      body: {
                        billingPeriod: planPeriod,
                        ...(enableSaasPricingPlanSelection
                          ? { plan: 'PRO' }
                          : {}),
                      },
                    })
                  }
                  break
                case 'ENTERPRISE':
                  if (user?.organizationId) {
                    setPlanLoadingStates({
                      starter: false,
                      enterprise: true,
                      pro: false,
                    })
                    await sendPostEnterpriseRequest({
                      orgId: user.organizationId,
                      body: {
                        intention: 'Onboarding tier selection - Web',
                      },
                    }).catch((error) => {
                      // Oh no. An error happened.
                      throw error
                    })

                    redirect()
                  }
                  break
              }
            }}
          />
        )}
      </div>
      {!showDynamicPricingPage && (
        <p className="font-support-medium asterisk-text align-center flex-column">
          {translate(
            '*Virtual card functionality for applicable bank partners',
          )}
        </p>
      )}
      <div className="footer flex-row justify-center">
        <BrandAgnosticFooter logOutOption topMargin={false} />
      </div>
    </div>
  )
}
